export function setCookie(name: string, value: string, expireHours = 2) {
    const exDate = new Date();
    exDate.setHours(exDate.getDate() + expireHours);
    if (expireHours > 0) {
        document.cookie = `${name}=${escape(value)};expires=${exDate.toUTCString()};path=/`;
    } else {
        document.cookie = `${name}=${escape(value)};path=/`;
    }
}

export function getCookie(name: string): string {
    if (document.cookie.length > 0) {
        let cStart = document.cookie.indexOf(name + '=');
        if (cStart !== -1) {
            cStart = cStart + name.length + 1;
            let cEnd = document.cookie.indexOf(';', cStart);
            if (cEnd === -1) {
                cEnd = document.cookie.length;
            }
            return unescape(document.cookie.substring(cStart, cEnd));
        }
    }
    return '';
}

/**
 * 清除cookie
 * @param name
 */
export function clearCookie(name: string) {
    setCookie(name, '');
}

export function queryURL(name: string): any {
    const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`, 'i');
    const result = window.location.search.substr(1).match(reg);
    if (result) {
        return decodeURIComponent(result[2]);
    }
    return null;
}

export function formatTime(time: number) {
    const s = time % 60;
    const m = parseInt(`${(time / 60) % 60}`);
    const h = parseInt(`${time / 60 / 60}`);
    return `${h < 10 ? `0${h}` : h}:${m < 10 ? `0${m}` : m}:${s < 10 ? `0${s}` : s}`;
}

export function to16kHz(audioData: Float32Array, sampleRate = 44100): Float32Array {
    const data = new Float32Array(audioData);
    const fitCount = Math.round(data.length * (16000 / sampleRate));
    const newData = new Float32Array(fitCount);
    const springFactor = (data.length - 1) / (fitCount - 1);
    newData[0] = data[0];
    for (let i = 0; i < fitCount - 1; i++) {
        const tmp = i * springFactor;
        const before = Number(Math.floor(tmp).toFixed());
        const after = Number(Math.ceil(tmp).toFixed());
        const atPoint = tmp - before;
        newData[i] = data[before] + (data[after] - data[before]) * atPoint;
    }
    newData[fitCount - 1] = data[data.length - 1];
    return newData;
}

export function to16BitPCM(input: Float32Array): DataView {
    const dataLength = input.length * (16 / 8);
    const dataBuffer = new ArrayBuffer(dataLength);
    const dataView = new DataView(dataBuffer);
    let offset = 0;
    for (let i = 0; i < input.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, input[i]));
        dataView.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return dataView;
}

export async function sleep(time: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, time));
}

export function isTimeout(baseTimeLine: number, timeout: number): boolean {
    const currentTime = new Date().getTime();
    const diffTIme = (currentTime - baseTimeLine) / 1000;
    return diffTIme > timeout;
}

export function getWindowHeight(): number {
    return (
        window.innerHeight ||
        (document.documentElement && document.documentElement.clientHeight) ||
        (document.body && document.body.clientHeight)
    );
}

export function getWindowWidth(): number {
    return (
        window.innerWidth ||
        (document.documentElement && document.documentElement.clientWidth) ||
        (document.body && document.body.clientWidth)
    );
}

export function getBizId(length: number = 32): string {
    const code = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678';
    let n: string = '';
    const a = code.length;
    for (let i = 0; i < length; i++) {
        n += code.charAt(Math.floor(Math.random() * a));
    }
    return n;
}

export function isIOS13(): boolean {
    const version = window.navigator.userAgent.toLowerCase().match(/cpu iphone os (.*?) like mac os/);
    if (!version) {
        return false;
    } else {
        const ver = parseInt(version[1].replace(/_/g, '.'), 10);
        if ((ver < 14 && ver >= 13) || ver === 15) {
            return true;
        } else {
            return false;
        }
    }
}

export function isIOS(): boolean {
    const u = navigator.userAgent;
    return !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/); //ios终端
}

export function encodeWAV(
    samples: Float32Array,
    sampleRate: number,
    format: number = 3,
    numChannels: number = 1,
    bitDepth: number = 32
): ArrayBuffer {
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const buffer = new ArrayBuffer(44 + samples.length * bytesPerSample);

    const view = new DataView(buffer);

    writeString(view, 0, 'RIFF');

    view.setUint32(4, 36 + samples.length * bytesPerSample, true);

    writeString(view, 8, 'WAVE');

    writeString(view, 12, 'fmt ');

    view.setUint32(16, 16, true);

    view.setUint16(20, format, true);

    view.setUint16(22, numChannels, true);

    view.setUint32(24, sampleRate, true);

    view.setUint32(28, sampleRate * blockAlign, true);

    view.setUint16(32, blockAlign, true);

    view.setUint16(34, bitDepth, true);

    writeString(view, 36, 'data');

    view.setUint32(40, samples.length * bytesPerSample, true);

    if (format === 1) {
        // Raw PCM
        floatTo16BitPCM(view, 44, samples);
    } else {
        writeFloat32(view, 44, samples);
    }

    return buffer;
}

function writeFloat32(output: DataView, offset: number, input: Float32Array) {
    for (let i = 0; i < input.length; i++, offset += 4) {
        output.setFloat32(offset, input[i], true);
    }
}

function floatTo16BitPCM(output: DataView, offset: number, input: Float32Array) {
    for (let i = 0; i < input.length; i++, offset += 2) {
        const s = Math.max(-1, Math.min(1, input[i]));
        output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
}

function writeString(view: DataView, offset: number, str: string) {
    for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
    }
}

export function measureText(): { width: number; height: number } {
    const span = document.createElement('span');
    span.style.fontSize = '4vw';
    span.style.visibility = 'hidden';
    span.textContent = '测';
    span.style.lineHeight = '1.5';
    document.body.appendChild(span);
    const lineHeight = window.getComputedStyle(span).lineHeight;
    const width = span.offsetWidth;
    const lineHeightNum = px2num(lineHeight);
    return { width, height: lineHeightNum > 0 ? lineHeightNum : span.offsetHeight };
}

function px2num(px: string): number {
    if (px) {
        const n = px
            .split('')
            .filter(p => p !== 'p')
            .filter(p => p !== 'x')
            .join('');
        return Number(n);
    }
    return 0;
}

export function applyScroll(el: HTMLElement, height: number, time: number) {
    el.scrollTo({
        left: 0,
        top: el.scrollTop + height,
        behavior: 'smooth'
    });

    const timer = setTimeout(() => {
        if (el.scrollTop < el.scrollHeight - el.offsetHeight) {
            applyScroll(el, height, time);
        }
    }, time);

    return () => {
        clearTimeout(timer);
    };
}

export function isWX(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    // @ts-ignore
    // eslint-disable-next-line eqeqeq
    if (ua.match(/MicroMessenger/i) == 'micromessenger') {
        return true;
    }

    return false;
}

export function getSafariVersion(): boolean {
    try {
        const ua = navigator.userAgent.toLowerCase();
        const result = ua.match(/os [\d._]*/gi);
        var version = (result + '').replace(/[^0-9|_.]/gi, '').replace(/_/gi, '.');
        const arr = version.split('.');
        if (parseInt(arr[0]) >= 14) {
            if (parseInt(arr[0]) === 14) {
                if (parseInt(arr[1]) >= 3) {
                    return true;
                }
            }
            return true;
        }

        return false;
    } catch (error) {
        return true;
    }
}

export function replaceLetters(str: string): string {
    // 使用正则表达式匹配所有连续的字母和数字组合，并将字母替换为指定字符，例如星号'*'
    return str.replace(/[A-Za-z]+/gi, match => {
        return match.split('').join(',');
    });
}

export function log(api: string, extra: string) {
    const img = new Image();
    img.src = `https://log.cloud-ins.cn/log.gif?type=verifyH5&api=${api}&extra=${extra}`;
}
